import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sprout, Shield, Scissors, CheckCircle, ArrowRight, Leaf, Clock, Users } from 'lucide-react';
import '../styles/ServicesPage.css';

const ServicesPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <Sprout size={50} />,
      title: "Fertilisation naturelle",
      tagline: "Pour une pelouse dense et en santé",
      description: "Notre programme de fertilisation 100% naturelle nourrit votre pelouse en profondeur avec des nutriments organiques de qualité supérieure. Contrairement aux engrais chimiques, nos produits naturels améliorent la structure du sol et favorisent une croissance durable.",
      features: [
        "Produits 100% naturels",
        "Programme saisonnier adapté au climat de l'Estrie",
        "Méthodes naturelles",
        "Sans danger pour les enfants et les animaux",
        "Résultats visibles en 2-3 semaines",
        "Croissance durable et résistante"
      ],
      process: [
        "Analyse de votre pelouse et du sol",
        "Sélection des nutriments adaptés",
        "Application uniforme professionnelle",
        "Suivi et ajustements saisonniers"
      ]
    },
    {
      icon: <Shield size={50} />,
      title: "Contrôle des mauvaises herbes naturel",
      tagline: "Élimination naturelle et efficace",
      description: "Notre approche naturelle du contrôle des mauvaises herbes combine prévention, traitement ciblé et renforcement de votre pelouse. Nous éliminons les indésirables tout en préservant la santé de votre gazon et de l'environnement.",
      features: [
        "Méthodes naturelles",
        "Traitement ciblé sans produits chimiques",
        "Prévention à long terme",
        "Respectueux de l'environnement",
        "Sécuritaire pour toute la famille",
        "Efficacité prouvée"
      ],
      process: [
        "Identification des types de mauvaises herbes",
        "Traitement naturel ciblé",
        "Densification de la pelouse",
        "Plan de prévention personnalisé"
      ]
    },
    {
      icon: <Scissors size={50} />,
      title: "Taille de haies professionnelle",
      tagline: "Précision et esthétique",
      description: "Nos experts en taille de haies transforment votre aménagement paysager avec un travail précis et soigné. Que ce soit pour des haies décoratives, des brise-vents ou des bordures, nous assurons une coupe nette et uniforme qui favorise la santé de vos arbustes.",
      features: [
        "Taille précise et uniforme",
        "Respect du cycle de croissance",
        "Nettoyage complet inclus",
        "Équipement professionnel",
        "Conseils d'entretien personnalisés",
        "Toutes formes et hauteurs"
      ],
      process: [
        "Évaluation de l'état des haies",
        "Taille professionnelle adaptée",
        "Ramassage et disposition des débris",
        "Recyclage des résidus"
      ]
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <h1 className="display-large text-center mb-md">Nos Services Professionnels</h1>
          <p className="body-large text-center" style={{maxWidth: '700px', margin: '0 auto'}}>
            Des solutions naturelles et professionnelles pour l'entretien complet de votre espace vert en Estrie.
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="services-detail section">
        <div className="container">
          {services.map((service, index) => (
            <div key={index} className={`service-detail-card ${index % 2 !== 0 ? 'reverse' : ''}`}>
              <div className="service-detail-content">
                <div className="service-detail-icon">{service.icon}</div>
                <h2 className="heading-1 mb-xs">{service.title}</h2>
                <p className="service-tagline mb-md">{service.tagline}</p>
                <p className="body-large mb-lg">{service.description}</p>

                <div className="features-grid">
                  <div className="features-section">
                    <h3 className="heading-3 mb-sm">
                      <Leaf size={24} /> Avantages
                    </h3>
                    <ul className="features-list">
                      {service.features.map((feature, idx) => (
                        <li key={idx}>
                          <CheckCircle size={18} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="features-section">
                    <h3 className="heading-3 mb-sm">
                      <Clock size={24} /> Notre processus
                    </h3>
                    <ol className="process-list">
                      {service.process.map((step, idx) => (
                        <li key={idx}>
                          <span className="process-number">{idx + 1}</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>

                <button className="btn-primary mt-lg" onClick={() => navigate('/contact')}>
                  Demander une soumission
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Natural Section */}
      <section className="why-natural-section section bg-subtle">
        <div className="container">
          <div className="why-natural-content">
            <h2 className="display-medium text-center mb-lg">
              Pourquoi choisir des solutions naturelles?
            </h2>
            
            <div className="benefits-grid">
              <div className="benefit-card nvf-card">
                <Users size={40} className="benefit-card-icon" />
                <h3 className="heading-3 mb-sm">Sécurité familiale</h3>
                <p className="body-medium">
                  Nos produits naturels sont sans danger pour vos enfants, vos animaux de compagnie et toute votre famille.
                </p>
              </div>

              <div className="benefit-card nvf-card">
                <Leaf size={40} className="benefit-card-icon" />
                <h3 className="heading-3 mb-sm">Respect de l'environnement</h3>
                <p className="body-medium">
                  Protégez la biodiversité locale et préservez la qualité de l'eau et du sol pour les générations futures.
                </p>
              </div>

              <div className="benefit-card nvf-card">
                <Sprout size={40} className="benefit-card-icon" />
                <h3 className="heading-3 mb-sm">Résultats durables</h3>
                <p className="body-medium">
                  Les solutions naturelles renforcent votre pelouse en profondeur pour des résultats qui durent toute la saison.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta section">
        <div className="container">
          <div className="cta-box">
            <h2 className="display-medium text-center mb-md">
              Prêt à profiter de nos services?
            </h2>
            <p className="body-large text-center mb-lg" style={{maxWidth: '600px', margin: '0 auto 32px', color: 'white'}}>
              Demandez votre soumission gratuite dès maintenant et découvrez comment nos services peuvent transformer votre espace extérieur.
            </p>
            <div className="text-center">
              <button className="btn-primary btn-large" onClick={() => navigate('/contact')}>
                Obtenir une soumission gratuite
                <ArrowRight size={22} />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
